import { useEffect, useState, useCallback, Fragment } from "react";
import api, { dkk, num, dec, parseAmount } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Package, Coins, ShoppingCart, AlertTriangle, Plus, Pencil, Trash2,
  Check, RefreshCw, TrendingUp, ChevronUp, ChevronDown, ChevronRight, Flag, StickyNote, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { DangoodsDeliveries } from "./DangoodsDeliveries";
import { BillsStats } from "./BillsStats";

const STORE_LABEL = { all: "Begge butikker", falster: "Falster", ringsted: "Ringsted" };
const supLabel = (s) => (s === "all" || !s ? "Alle leverandører" : s);
const STMT_LABEL = { falster: "Lagersalg Falster", ringsted: "Lagersalg Ringsted" };

function mondayThisWeek() {
  const d = new Date();
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return d;
}
const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function Card({ icon: Icon, title, value, sub, tone = "sky", testId }) {
  const tones = {
    sky: "text-sky-600", green: "text-emerald-600",
    amber: "text-amber-600", slate: "text-slate-700",
  };
  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-slate-500 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-[13px] font-semibold uppercase tracking-[0.14em]">{title}</span>
      </div>
      <div className={`font-heading text-3xl font-bold tabular-nums ${tones[tone]}`}>{value}</div>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

const EMPTY = { store: "all", supplier: "all", period_from: "", period_to: "", amount: "", status: "ikke_afregnet", note: "" };

export function DangoodsView({ store = "all", admin = false }) {
  const [from, setFrom] = useState(iso(mondayThisWeek()));
  const [to, setTo] = useState(iso(new Date()));
  const [supplier, setSupplier] = useState("all");
  const [suppliersList, setSuppliersList] = useState([]);
  const [stockSearch, setStockSearch] = useState("");
  const [stockStatus, setStockStatus] = useState("all");
  const [stockCategory, setStockCategory] = useState("all");
  const [stockStore, setStockStore] = useState("all");
  const [stockSort, setStockSort] = useState({ key: "stock_value", dir: "desc" });
  const [stockMarked, setStockMarked] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [noteDrafts, setNoteDrafts] = useState({});
  const [showHidden, setShowHidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [stock, setStock] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [statements, setStatements] = useState([]);
  const [stmtEdits, setStmtEdits] = useState({});
  const [sub, setSub] = useState("sold"); // sold | stock | settlements
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bd, setBd] = useState(null);
  const [bdLoading, setBdLoading] = useState(false);

  const openBreakdown = async (w) => {
    setBd({ week: w, data: null });
    setBdLoading(true);
    try {
      const r = await api.get("/dangoods/weekly/breakdown", { params: { store, supplier, week_start: w.week_start } });
      setBd({ week: w, data: r.data });
    } catch {
      toast.error("Kunne ikke hente specifikation");
      setBd(null);
    } finally { setBdLoading(false); }
  };

  const [rebuilding, setRebuilding] = useState(false);
  const rebuildWeek = async (w) => {
    setRebuilding(true);
    try {
      await api.post("/dangoods/rebuild-week", null, { params: { store, week_start: w.week_start } });
      toast.success("Genopbygning startet — opdaterer om et øjeblik");
      setBd(null);
      setTimeout(() => { load(); }, 60000);
    } catch {
      toast.error("Kunne ikke starte genopbygning");
    } finally { setRebuilding(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, st, se, sup, stm, wk] = await Promise.all([
        api.get("/dangoods/overview", { params: { store, supplier, from, to } }),
        api.get("/dangoods/products", { params: { store, supplier } }),
        api.get("/dangoods/settlements"),
        api.get("/dangoods/suppliers", { params: { store } }),
        api.get("/dangoods/statements"),
        api.get("/dangoods/weekly", { params: { store, supplier } }),
      ]);
      setOverview(ov.data);
      setStock(st.data);
      setSettlements(se.data);
      setSuppliersList(sup.data || []);
      setStatements(stm.data || []);
      setWeekly(wk.data?.weeks || []);
      // Bevar brugerens indtastning: udfyld kun felter der ikke allerede har en værdi.
      setStmtEdits((prev) => {
        const next = { ...prev };
        (stm.data || []).forEach((r) => {
          if (next[r.store] === undefined || next[r.store] === "") {
            next[r.store] = r.statement_amount ? dec(r.statement_amount) : (next[r.store] ?? "");
          }
        });
        return next;
      });
    } catch {
      toast.error("Kunne ikke hente Dangoods-data.");
    } finally {
      setLoading(false);
    }
  }, [store, supplier, from, to]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSupplier("all"); }, [store]);

  const openNew = () => {
    setEditId(null);
    setForm({
      ...EMPTY, store, supplier: supplier === "all" ? "all" : supplier,
      period_from: from, period_to: to,
      amount: overview ? dec(overview.settlement_suggestion) : "",
    });
    setDlgOpen(true);
  };
  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      store: s.store, supplier: s.supplier || "all", period_from: s.period_from, period_to: s.period_to,
      amount: String(s.amount), status: s.status, note: s.note || "",
    });
    setDlgOpen(true);
  };

  const save = async () => {
    const amount = parseAmount(form.amount);
    if (!form.period_from || !form.period_to) { toast.error("Vælg periode"); return; }
    if (isNaN(amount) || amount < 0) { toast.error("Ugyldigt beløb"); return; }
    setSaving(true);
    const body = { ...form, amount };
    try {
      if (editId) await api.put(`/dangoods/settlements/${editId}`, body);
      else await api.post("/dangoods/settlements", body);
      setDlgOpen(false);
      toast.success(editId ? "Afregning opdateret" : "Afregning oprettet");
      load();
    } catch (e) {
      toast.error("Kunne ikke gemme afregning");
    } finally {
      setSaving(false);
    }
  };

  const togglePaid = async (s) => {
    try {
      await api.put(`/dangoods/settlements/${s.id}`, {
        store: s.store, supplier: s.supplier || "all", period_from: s.period_from, period_to: s.period_to,
        amount: s.amount, note: s.note,
        status: s.status === "afregnet" ? "ikke_afregnet" : "afregnet",
      });
      load();
    } catch { toast.error("Kunne ikke opdatere status"); }
  };

  const remove = async (s) => {
    if (!window.confirm("Slet denne afregning?")) return;
    try { await api.delete(`/dangoods/settlements/${s.id}`); toast.success("Slettet"); load(); }
    catch { toast.error("Kunne ikke slette"); }
  };

  const saveStatement = async (store) => {
    const amount = parseAmount(stmtEdits[store]);
    if (isNaN(amount) || amount < 0) { toast.error("Ugyldigt beløb"); return; }
    try {
      await api.put(`/dangoods/statements/${store}`, { amount });
      toast.success("Kontoudtog gemt");
      load();
    } catch { toast.error("Kunne ikke gemme kontoudtog"); }
  };

  const toggleMark = async (p) => {
    const newMarked = !p.marked;
    setStock((prev) => prev.map((x) => (x.store === p.store && x.productno === p.productno
      ? { ...x, marked: newMarked, note: newMarked ? x.note : "" } : x)));
    try {
      await api.post("/dangoods/mark", { store: p.store, productno: p.productno, marked: newMarked, note: p.note || "" });
    } catch {
      toast.error("Kunne ikke markere varen");
      setStock((prev) => prev.map((x) => (x.store === p.store && x.productno === p.productno
        ? { ...x, marked: p.marked } : x)));
    }
  };

  const saveNote = async (p, note) => {
    try {
      await api.post("/dangoods/mark", { store: p.store, productno: p.productno, marked: true, note });
      setStock((prev) => prev.map((x) => (x.store === p.store && x.productno === p.productno ? { ...x, note } : x)));
    } catch { toast.error("Kunne ikke gemme note"); }
  };

  const hideProduct = async (p) => {
    const newHidden = !p.hidden;
    setStock((prev) => prev.map((x) => (x.store === p.store && x.productno === p.productno ? { ...x, hidden: newHidden } : x)));
    try {
      await api.post("/dangoods/hide", { store: p.store, productno: p.productno, hidden: newHidden });
      toast.success(newHidden ? "Vare skjult" : "Vare vist igen");
    } catch {
      toast.error("Kunne ikke skjule varen");
      setStock((prev) => prev.map((x) => (x.store === p.store && x.productno === p.productno ? { ...x, hidden: p.hidden } : x)));
    }
  };

  const toggleWeekly = async (w) => {
    try {
      await api.put("/dangoods/weekly/status", {
        store, supplier, week_start: w.week_start,
        status: w.status === "afregnet" ? "ikke_afregnet" : "afregnet",
      });
      load();
    } catch { toast.error("Kunne ikke opdatere status"); }
  };

  const sales = overview?.sales;
  const stk = overview?.stock;
  const outOfStock = stock.filter((p) => (p.stock || 0) <= 0);

  const unsettled = settlements.filter((s) => s.status === "ikke_afregnet");
  const owed = (sup) => unsettled
    .filter((s) => (sup === "all" ? true : s.supplier === sup))
    .reduce((a, s) => a + (s.amount || 0), 0);
  const owedTotal = owed("all");

  const totalStockValue = statements.reduce((a, r) => a + (r.stock_value || 0), 0);
  const totalStatement = statements.reduce((a, r) => a + (r.statement_amount || 0), 0);
  const afregningKost = overview?.sales?.total_cost || 0;
  const totalDiff = totalStockValue - totalStatement + afregningKost;

  const STOCK_NUMERIC = new Set(["stock", "costprice", "stock_value"]);
  const toggleSort = (key) => setStockSort((cur) => cur.key === key
    ? { key, dir: cur.dir === "asc" ? "desc" : "asc" }
    : { key, dir: STOCK_NUMERIC.has(key) ? "desc" : "asc" });
  const sortRows = (rows) => {
    const { key, dir } = stockSort;
    const s = [...rows].sort((a, b) => STOCK_NUMERIC.has(key)
      ? (a[key] || 0) - (b[key] || 0)
      : String(a[key] || "").localeCompare(String(b[key] || ""), "da"));
    return dir === "desc" ? s.reverse() : s;
  };
  const SortIcon = ({ k }) => stockSort.key !== k ? null
    : (stockSort.dir === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />);

  const soldProducts = sales?.products || [];
  const groups = supplier === "all" ? suppliersList : [supplier];
  const stockCategories = [...new Set(stock.map((p) => p.category).filter(Boolean))].sort();
  const stockFiltered = stock.filter((p) => {
    const st = p.stock || 0;
    if (stockStatus === "instock" && st <= 0) return false;
    if (stockStatus === "missing" && st > 0) return false;
    if (stockCategory !== "all" && p.category !== stockCategory) return false;
    if (stockStore !== "all" && p.store !== stockStore) return false;
    if (stockMarked && !p.marked) return false;
    if (!showHidden && p.hidden) return false;
    if (stockSearch) {
      const q = stockSearch.toLowerCase();
      if (!`${p.title} ${p.productno} ${p.category} ${p.ean || ""}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-5" data-testid="dangoods-view">
      {/* Period + refresh */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Leverandør</Label>
          <Select value={supplier} onValueChange={setSupplier}>
            <SelectTrigger className="h-9 mt-1 w-[180px] bg-[#ffffff] border-[#e2e8f0] text-slate-700" data-testid="dangoods-supplier-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-700">
              <SelectItem value="all">Alle leverandører</SelectItem>
              {suppliersList.map((name) => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Fra</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="h-9 mt-1 bg-[#ffffff] border-[#e2e8f0] text-slate-700" data-testid="dangoods-from-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Til</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="h-9 mt-1 bg-[#ffffff] border-[#e2e8f0] text-slate-700" data-testid="dangoods-to-input" />
        </div>
        <Button variant="outline" onClick={() => { setFrom(iso(mondayThisWeek())); setTo(iso(new Date())); }}
          className="h-9 bg-[#ffffff] border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] hover:text-slate-900" data-testid="dangoods-thisweek-btn">
          Denne uge
        </Button>
        {admin && (
          <Button variant="outline" onClick={async () => { try { await api.post("/dangoods/sync"); toast.success("Lager-synk startet"); } catch { toast.error("Kunne ikke synke"); } }}
            className="h-9 bg-[#ffffff] border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] hover:text-slate-900 gap-2" data-testid="dangoods-sync-btn">
            <RefreshCw className="w-4 h-4" /> Synk lager
          </Button>
        )}
        <span className="ml-auto text-xs text-slate-500">
          {stk?.last_sync ? `Lager opdateret ${new Date(stk.last_sync).toLocaleString("da-DK", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : ""}
        </span>
      </div>

      {/* Outstanding (ikke afregnet) banner */}
      {owedTotal > 0 && (
        <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="dangoods-owed-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-amber-600/80">Skyldigt i alt (ikke afregnet)</p>
              <p className="font-heading text-3xl font-bold tabular-nums text-amber-700" data-testid="dangoods-owed-total">{dkk(owedTotal)}</p>
            </div>
          </div>
          <div className="sm:ml-auto flex flex-wrap gap-6">
            {suppliersList.map((name) => (
              owed(name) > 0 && (
                <div key={name} data-testid={`dangoods-owed-${name}`}>
                  <p className="text-xs uppercase tracking-wider text-amber-600/60">{name}</p>
                  <p className="font-heading text-xl font-bold tabular-nums text-amber-700">{dkk(owed(name))}</p>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Samlet difference (lagerværdi − kontoudtog − afregning) */}
      {statements.length > 0 && (
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="dangoods-total-diff">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500/15 flex items-center justify-center">
              <Coins className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500">Samlet difference (lager − kontoudtog + afregning)</p>
              <p className={`font-heading text-3xl font-bold tabular-nums ${totalDiff >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="dangoods-total-diff-value">{dkk(totalDiff)}</p>
            </div>
          </div>
          <div className="sm:ml-auto flex flex-wrap gap-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">Samlet lagerværdi</p>
              <p className="font-heading text-xl font-bold tabular-nums text-slate-700">{dkk(totalStockValue)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">Samlet kontoudtog</p>
              <p className="font-heading text-xl font-bold tabular-nums text-slate-700">{dkk(totalStatement)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">+ Afregning (kost)</p>
              <p className="font-heading text-xl font-bold tabular-nums text-amber-600" data-testid="dangoods-total-diff-afregning">{dkk(afregningKost)}</p>
            </div>
            {statements.map((r) => (
              <div key={r.store} data-testid={`dangoods-total-diff-${r.store}`}>
                <p className="text-xs uppercase tracking-wider text-slate-500">{STMT_LABEL[r.store] || r.store}</p>
                <p className={`font-heading text-xl font-bold tabular-nums ${r.difference >= 0 ? "text-emerald-600" : "text-red-600"}`}>{dkk(r.difference)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card icon={Coins} title="Afregning (kost)" value={dkk(sales?.total_cost)} tone="green"
          sub="Kostpris inkl. moms af solgte varer i perioden" testId="dangoods-kpi-cost" />
        <Card icon={TrendingUp} title="Omsætning" value={dkk(sales?.total_revenue)} tone="sky"
          sub={`${num(sales?.total_qty || 0)} varer solgt`} testId="dangoods-kpi-revenue" />
        <Card icon={Package} title="Lagerværdi" value={dkk(stk?.stock_value)} tone="slate"
          sub={`${num(stk?.products_total || 0)} varenumre · inkl. moms`} testId="dangoods-kpi-stockvalue" />
        <Card icon={AlertTriangle} title="Mangler på lager" value={num(stk?.out_of_stock || 0)} tone="amber"
          sub={`${num(stk?.in_stock || 0)} varer har lager`} testId="dangoods-kpi-outofstock" />
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-[#e2e8f0] overflow-x-auto">
        {[["sold", "Solgt i perioden"], ["stock", "Lagerbeholdning"], ["settlements", "Afregninger"], ["kontoudtog", "Kontoudtog"], ["leveringer", "Leveringer"], ["regninger", "Regninger"]].map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${sub === id ? "border-[#38BDF8] text-[#0369a1]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`dangoods-subtab-${id}`}>
            {label}
          </button>
        ))}
      </div>

      {loading && <p className="text-slate-500 text-sm">Henter…</p>}

      {/* Sold */}
      {!loading && sub === "sold" && (
        <div className="space-y-6" data-testid="dangoods-sold">
          {groups.map((gid) => {
            const label = gid;
            const rows = soldProducts.filter((p) => p.supplier === label);
            if (rows.length === 0) return null;
            const gCost = rows.reduce((a, p) => a + (p.cost || 0), 0);
            const gRev = rows.reduce((a, p) => a + (p.revenue || 0), 0);
            return (
              <div key={gid} data-testid={`dangoods-sold-${gid}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <h4 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-700">{label}</h4>
                  <p className="text-xs text-slate-500">Afregning inkl. moms <span className="text-emerald-600 font-semibold">{dkk(gCost)}</span> · Omsætning {dkk(gRev)}</p>
                </div>
                <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] overflow-x-auto">
                  <table className="w-full text-sm min-w-[680px]">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-[#e2e8f0]">
                        <th className="px-4 py-3 font-medium">Vare</th>
                        <th className="px-4 py-3 font-medium">Stregkode (EAN)</th>
                        <th className="px-4 py-3 font-medium">Kategori</th>
                        <th className="px-4 py-3 font-medium text-right">Antal</th>
                        <th className="px-4 py-3 font-medium text-right">Omsætning</th>
                        <th className="px-4 py-3 font-medium text-right">Kost inkl. moms (afregning)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((p) => (
                        <tr key={p.productno} className="border-b border-[#e2e8f0] last:border-0">
                          <td className="px-4 py-2.5 text-slate-700">{p.title}</td>
                          <td className="px-4 py-2.5 tabular-nums text-slate-500 font-mono text-xs">{p.ean || "—"}</td>
                          <td className="px-4 py-2.5 text-slate-500">{p.category}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{num(p.qty)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{dkk(p.revenue)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-emerald-600 font-medium">{dkk(p.cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
          {soldProducts.length === 0 && (
            <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] px-4 py-8 text-center text-slate-500">Ingen salg i perioden.</div>
          )}
        </div>
      )}

      {/* Stock */}
      {!loading && sub === "stock" && (
        <div className="space-y-4" data-testid="dangoods-stock">
          <div className="flex flex-wrap items-center gap-3">
            <Input value={stockSearch} onChange={(e) => setStockSearch(e.target.value)}
              placeholder="Søg vare eller varenr…"
              className="h-9 w-full sm:w-[220px] bg-[#ffffff] border-[#e2e8f0] text-slate-700" data-testid="dangoods-stock-search" />
            <Select value={stockStatus} onValueChange={setStockStatus}>
              <SelectTrigger className="h-9 w-[170px] bg-[#ffffff] border-[#e2e8f0] text-slate-700" data-testid="dangoods-stock-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-700">
                <SelectItem value="all">Alle varer</SelectItem>
                <SelectItem value="instock">På lager</SelectItem>
                <SelectItem value="missing">Mangler på lager</SelectItem>
              </SelectContent>
            </Select>
            <Select value={stockCategory} onValueChange={setStockCategory}>
              <SelectTrigger className="h-9 w-[170px] bg-[#ffffff] border-[#e2e8f0] text-slate-700" data-testid="dangoods-stock-category"><SelectValue placeholder="Kategori" /></SelectTrigger>
              <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-700 max-h-[300px]">
                <SelectItem value="all">Alle kategorier</SelectItem>
                {stockCategories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
            {store === "all" && (
              <Select value={stockStore} onValueChange={setStockStore}>
                <SelectTrigger className="h-9 w-[150px] bg-[#ffffff] border-[#e2e8f0] text-slate-700" data-testid="dangoods-stock-store"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-700">
                  <SelectItem value="all">Begge butikker</SelectItem>
                  <SelectItem value="falster">Falster</SelectItem>
                  <SelectItem value="ringsted">Ringsted</SelectItem>
                </SelectContent>
              </Select>
            )}
            {(stockSearch || stockStatus !== "all" || stockCategory !== "all" || stockStore !== "all" || stockMarked || showHidden) && (
              <Button variant="ghost" onClick={() => { setStockSearch(""); setStockStatus("all"); setStockCategory("all"); setStockStore("all"); setStockMarked(false); setShowHidden(false); }}
                className="h-9 text-slate-500 hover:text-slate-900" data-testid="dangoods-stock-clear">Nulstil</Button>
            )}
            <Button variant="outline" onClick={() => setStockMarked((v) => !v)}
              className={`h-9 gap-2 ${stockMarked ? "bg-amber-400 text-[#0b1120] border-amber-400 hover:bg-amber-300" : "bg-[#ffffff] border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] hover:text-slate-900"}`}
              data-testid="dangoods-stock-marked-filter">
              <Flag className="w-4 h-4" /> Kun markerede
            </Button>
            <Button variant="outline" onClick={() => setShowHidden((v) => !v)}
              className={`h-9 gap-2 ${showHidden ? "bg-slate-500 text-white border-slate-400 hover:bg-slate-400" : "bg-[#ffffff] border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] hover:text-slate-900"}`}
              data-testid="dangoods-stock-hidden-filter">
              {showHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />} Vis skjulte
            </Button>
            <span className="text-xs text-slate-500 ml-auto">{num(stockFiltered.length)} varer</span>
          </div>
          {groups.map((gid) => {
            const label = gid;
            const rows = stockFiltered.filter((p) => p.supplier === label);
            if (rows.length === 0) return null;
            const gVal = rows.reduce((a, p) => a + (p.stock_value || 0), 0);
            const gMissing = rows.filter((p) => (p.stock || 0) <= 0).length;
            const gMarked = rows.filter((p) => p.marked).length;
            const open = !!openGroups[gid];
            const noteCols = store === "all" ? 7 : 6;
            return (
              <div key={gid} data-testid={`dangoods-stock-${gid}`}>
                <button onClick={() => setOpenGroups((o) => ({ ...o, [gid]: !o[gid] }))}
                  data-testid={`dangoods-stock-toggle-${gid}`}
                  className="w-full flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[#ffffff] border border-[#e2e8f0] px-4 py-3 hover:border-[#38BDF8]/40 transition-colors text-left">
                  <span className="flex items-center gap-2 min-w-0">
                    {open ? <ChevronDown className="w-4 h-4 text-[#38BDF8] shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                    <span className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-800 truncate">{label}</span>
                    <span className="text-xs text-slate-500 shrink-0">({num(rows.length)})</span>
                    {gMarked > 0 && <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-600 border border-amber-400/30 shrink-0">{gMarked} markeret</span>}
                  </span>
                  <span className="text-xs text-slate-500 shrink-0">Lagerværdi <span className="text-slate-700 font-semibold">{dkk(gVal)}</span> · {num(gMissing)} mangler</span>
                </button>
                {open && (
                <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] overflow-x-auto mt-2">
                  <table className="w-full text-sm min-w-[680px]">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-[#e2e8f0]">
                        <th className="px-3 py-3 font-medium w-12 text-center" title="Send til Dangoods"><Flag className="w-3.5 h-3.5 inline" /></th>
                        <th className="px-4 py-3 font-medium cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("title")} data-testid="dangoods-sort-title"><span className="inline-flex items-center gap-1">Vare <SortIcon k="title" /></span></th>
                        <th className="px-4 py-3 font-medium">Stregkode (EAN)</th>
                        {store === "all" && <th className="px-4 py-3 font-medium cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("store_label")} data-testid="dangoods-sort-store"><span className="inline-flex items-center gap-1">Butik <SortIcon k="store_label" /></span></th>}
                        <th className="px-4 py-3 font-medium cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("category")} data-testid="dangoods-sort-category"><span className="inline-flex items-center gap-1">Kategori <SortIcon k="category" /></span></th>
                        <th className="px-4 py-3 font-medium text-right cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("stock")} data-testid="dangoods-sort-stock"><span className="inline-flex items-center gap-1 justify-end w-full">På lager <SortIcon k="stock" /></span></th>
                        <th className="px-4 py-3 font-medium text-right cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("costprice")} data-testid="dangoods-sort-costprice"><span className="inline-flex items-center gap-1 justify-end w-full">Kostpris inkl. moms <SortIcon k="costprice" /></span></th>
                        <th className="px-4 py-3 font-medium text-right cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("stock_value")} data-testid="dangoods-sort-value"><span className="inline-flex items-center gap-1 justify-end w-full">Lagerværdi <SortIcon k="stock_value" /></span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortRows(rows).map((p) => {
                        const low = (p.stock || 0) <= 0;
                        const key = `${p.store}:${p.productno}`;
                        return (
                          <Fragment key={key}>
                          <tr className={`border-b border-[#e2e8f0] ${p.marked ? "bg-amber-500/10" : ""} ${p.hidden ? "opacity-40" : ""} ${p.marked ? "" : "last:border-0"}`}>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => toggleMark(p)} title={p.marked ? "Fjern markering" : "Marker (skal sendes)"}
                                  data-testid={`dangoods-mark-${p.store}-${p.productno}`}
                                  className={`w-6 h-6 rounded-md inline-flex items-center justify-center transition-colors ${p.marked ? "bg-amber-400 text-[#0b1120]" : "bg-[#f8fafc] border border-[#e2e8f0] text-slate-500 hover:text-slate-600"}`}>
                                  <Flag className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => hideProduct(p)} title={p.hidden ? "Vis vare igen" : "Skjul vare (udgået)"}
                                  data-testid={`dangoods-hide-${p.store}-${p.productno}`}
                                  className="w-6 h-6 rounded-md inline-flex items-center justify-center bg-[#f8fafc] border border-[#e2e8f0] text-slate-500 hover:text-slate-600 transition-colors">
                                  {p.hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-slate-700">{p.title}</td>
                            <td className="px-4 py-2.5 tabular-nums text-slate-500 font-mono text-xs">{p.ean || "—"}</td>
                            {store === "all" && <td className="px-4 py-2.5 text-slate-500">{p.store_label}</td>}
                            <td className="px-4 py-2.5 text-slate-500">{p.category}</td>
                            <td className={`px-4 py-2.5 text-right tabular-nums font-medium ${low ? "text-amber-600" : "text-slate-500"}`}>
                              {low && <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}{num(p.stock)}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{dkk(p.costprice)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{dkk(p.stock_value)}</td>
                          </tr>
                          {p.marked && (
                            <tr className="bg-amber-500/5 border-b border-[#e2e8f0] last:border-0">
                              <td className="px-3 py-2"></td>
                              <td className="px-4 py-2" colSpan={noteCols}>
                                <div className="flex items-center gap-2">
                                  <StickyNote className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                  <Input
                                    value={noteDrafts[key] !== undefined ? noteDrafts[key] : (p.note || "")}
                                    onChange={(e) => setNoteDrafts((d) => ({ ...d, [key]: e.target.value }))}
                                    onBlur={(e) => { if (e.target.value !== (p.note || "")) saveNote(p, e.target.value); }}
                                    placeholder="Skriv en note til denne vare…"
                                    className="h-8 bg-[#f8fafc] border-[#e2e8f0] text-slate-700 text-xs"
                                    data-testid={`dangoods-note-${p.store}-${p.productno}`} />
                                </div>
                              </td>
                            </tr>
                          )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            );
          })}
          {stockFiltered.length === 0 && (
            <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] px-4 py-8 text-center text-slate-500">
              Ingen varer matcher filteret. {admin && stock.length === 0 && "Tryk “Synk lager”."}
            </div>
          )}
        </div>
      )}

      {/* Settlements */}
      {!loading && sub === "settlements" && (
        <div className="space-y-4" data-testid="dangoods-settlements">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-700">Ugentlig afregning (automatisk)</h4>
            <p className="text-xs text-slate-500">Beløbet beregnes automatisk pr. uge = kostpris inkl. moms af solgte varer. {supplier === "all" ? "Alle leverandører" : supplier}{store !== "all" ? ` · ${STORE_LABEL[store]}` : ""}</p>
            <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-[#e2e8f0]">
                    <th className="px-4 py-3 font-medium">Uge</th>
                    <th className="px-4 py-3 font-medium">Periode</th>
                    <th className="px-4 py-3 font-medium text-right">Antal</th>
                    <th className="px-4 py-3 font-medium text-right">Omsætning</th>
                    <th className="px-4 py-3 font-medium text-right">Afregning</th>
                    <th className="px-4 py-3 font-medium text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {weekly.map((w) => (
                    <tr key={w.week_start} className="border-b border-[#e2e8f0] last:border-0" data-testid={`dangoods-week-${w.week_start}`}>
                      <td className="px-4 py-2.5 text-slate-700 font-medium whitespace-nowrap">
                        <button onClick={() => openBreakdown(w)} data-testid={`dangoods-week-spec-${w.week_start}`}
                          className="text-slate-700 hover:text-sky-600 underline decoration-dotted underline-offset-4 transition-colors">
                          Uge {w.week_no}
                        </button>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 tabular-nums whitespace-nowrap">{w.week_start} → {w.week_end}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{num(w.qty)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{dkk(w.revenue)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-600 font-medium">{dkk(w.amount)}</td>
                      <td className="px-4 py-2.5 text-center">
                        {admin ? (
                          <button onClick={() => toggleWeekly(w)} data-testid={`dangoods-week-toggle-${w.week_start}`}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${w.status === "afregnet" ? "bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25" : "bg-amber-500/15 text-amber-600 hover:bg-amber-500/25"}`}>
                            {w.status === "afregnet" ? "Afregnet" : "Ikke afregnet"}
                          </button>
                        ) : (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${w.status === "afregnet" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
                            {w.status === "afregnet" ? "Afregnet" : "Ikke afregnet"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {weekly.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Ingen ugedata endnu.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-500">Klik på et ugenummer for at se specifikationen (antal × kostpris pr. vare).</p>
          </div>
          {admin && (
            <p className="text-xs uppercase tracking-wider text-slate-500 pt-2 border-t border-[#e2e8f0]">Manuelle afregninger</p>
          )}
          {admin && (
            <Button onClick={openNew} className="rounded-lg bg-[#38BDF8] text-[#0b1120] hover:bg-sky-400 gap-2" data-testid="dangoods-new-settlement-btn">
              <Plus className="w-4 h-4" /> Ny afregning
            </Button>
          )}
          <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead>
                <tr className="text-left text-slate-500 border-b border-[#e2e8f0]">
                  <th className="px-4 py-3 font-medium">Periode</th>
                  <th className="px-4 py-3 font-medium">Butik</th>
                  <th className="px-4 py-3 font-medium">Leverandør</th>
                  <th className="px-4 py-3 font-medium text-right">Beløb</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Note</th>
                  {admin && <th className="px-4 py-3 font-medium text-right">Handling</th>}
                </tr>
              </thead>
              <tbody>
                {settlements.map((s) => (
                  <tr key={s.id} className="border-b border-[#e2e8f0] last:border-0" data-testid={`dangoods-settlement-${s.id}`}>
                    <td className="px-4 py-2.5 text-slate-700 tabular-nums">{s.period_from} → {s.period_to}</td>
                    <td className="px-4 py-2.5 text-slate-500">{STORE_LABEL[s.store] || s.store}</td>
                    <td className="px-4 py-2.5 text-slate-500">{supLabel(s.supplier)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{dkk(s.amount)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.status === "afregnet" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
                        {s.status === "afregnet" ? "Afregnet" : "Ikke afregnet"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 max-w-[220px] truncate">{s.note}</td>
                    {admin && (
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => togglePaid(s)} title="Skift status"
                            className="h-8 w-8 text-slate-500 hover:text-emerald-600" data-testid={`dangoods-toggle-${s.id}`}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(s)}
                            className="h-8 w-8 text-slate-500 hover:text-sky-600" data-testid={`dangoods-edit-${s.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(s)}
                            className="h-8 w-8 text-slate-500 hover:text-red-600" data-testid={`dangoods-delete-${s.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {settlements.length === 0 && (
                  <tr><td colSpan={admin ? 7 : 6} className="px-4 py-8 text-center text-slate-500">Ingen afregninger endnu.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && sub === "leveringer" && (
        <DangoodsDeliveries suppliers={suppliersList} />
      )}

      {sub === "regninger" && (
        <BillsStats scope="dangoods" />
      )}

      {/* Kontoudtog */}
      {!loading && sub === "kontoudtog" && (
        <div className="space-y-4" data-testid="dangoods-kontoudtog">
          <p className="text-sm text-slate-500">Tast det skyldige beløb ind jf. kontoudtog for hver butik. Difference = samlet lagerværdi (inkl. moms) − kontoudtog.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {statements.map((row) => {
              const amt = parseAmount(stmtEdits[row.store] ?? "") || 0;
              const diff = (row.stock_value || 0) - amt;
              return (
                <div key={row.store} className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5 space-y-4" data-testid={`dangoods-kontoudtog-${row.store}`}>
                  <h4 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-700">{STMT_LABEL[row.store] || row.store}</h4>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 text-sm">Lagerværdi (inkl. moms)</span>
                    <span className="tabular-nums text-slate-700 font-medium" data-testid={`kontoudtog-stockvalue-${row.store}`}>{dkk(row.stock_value)}</span>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Skyldigt jf. kontoudtog (kr)</Label>
                    <Input value={stmtEdits[row.store] ?? ""} onChange={(e) => setStmtEdits({ ...stmtEdits, [row.store]: e.target.value })}
                      placeholder="0"
                      className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid={`kontoudtog-input-${row.store}`} />
                  </div>
                  <div className="flex items-center justify-between border-t border-[#e2e8f0] pt-3">
                    <span className="text-slate-500 text-sm font-medium">Difference</span>
                    <span className={`tabular-nums text-xl font-semibold ${diff >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`kontoudtog-diff-${row.store}`}>{dkk(diff)}</span>
                  </div>
                  <Button onClick={() => saveStatement(row.store)} className="w-full rounded-lg bg-[#38BDF8] text-[#0b1120] hover:bg-sky-400" data-testid={`kontoudtog-save-${row.store}`}>
                    Gem kontoudtog
                  </Button>
                  {row.updated && (
                    <p className="text-xs text-slate-500">Opdateret {new Date(row.updated).toLocaleString("da-DK", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Settlement dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800" data-testid="dangoods-settlement-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Rediger afregning" : "Ny afregning"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Fra</Label>
                <Input type="date" value={form.period_from} onChange={(e) => setForm({ ...form, period_from: e.target.value })}
                  className="mt-1 bg-[#f8fafc] border-[#e2e8f0]" data-testid="settlement-from-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Til</Label>
                <Input type="date" value={form.period_to} onChange={(e) => setForm({ ...form, period_to: e.target.value })}
                  className="mt-1 bg-[#f8fafc] border-[#e2e8f0]" data-testid="settlement-to-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Butik</Label>
                <Select value={form.store} onValueChange={(v) => setForm({ ...form, store: v })}>
                  <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e2e8f0]" data-testid="settlement-store-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
                    <SelectItem value="all">Begge butikker</SelectItem>
                    <SelectItem value="falster">Falster</SelectItem>
                    <SelectItem value="ringsted">Ringsted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Leverandør</Label>
                <Select value={form.supplier} onValueChange={(v) => setForm({ ...form, supplier: v })}>
                  <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e2e8f0]" data-testid="settlement-supplier-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
                    <SelectItem value="all">Alle leverandører</SelectItem>
                    {suppliersList.map((name) => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Beløb (kr)</Label>
                <Input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="mt-1 bg-[#f8fafc] border-[#e2e8f0]" data-testid="settlement-amount-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e2e8f0]" data-testid="settlement-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
                    <SelectItem value="ikke_afregnet">Ikke afregnet</SelectItem>
                    <SelectItem value="afregnet">Afregnet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="mt-1 bg-[#f8fafc] border-[#e2e8f0]" data-testid="settlement-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlgOpen(false)} className="text-slate-500">Annuller</Button>
            <Button onClick={save} disabled={saving} className="bg-[#38BDF8] text-[#0b1120] hover:bg-sky-400" data-testid="settlement-save-btn">
              {saving ? "Gemmer…" : "Gem"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!bd} onOpenChange={(o) => !o && setBd(null)}>
        <DialogContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800 max-w-3xl" data-testid="dangoods-breakdown-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-light">
              Specifikation — Uge {bd?.week?.week_no} ({bd?.week?.week_start} → {bd?.week?.week_end})
            </DialogTitle>
          </DialogHeader>
          {bdLoading && <p className="text-sm text-slate-500 py-6 text-center">Henter specifikation…</p>}
          {!bdLoading && bd?.data && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                <span className="text-slate-500">{bd.data.product_count} varer · {num(bd.data.total_qty)} stk</span>
                <span className="text-slate-500">Omsætning <span className="text-slate-700">{dkk(bd.data.total_revenue)}</span></span>
                <span className="ml-auto text-slate-500">Afregning i alt (inkl. moms): <span className="text-emerald-600 font-semibold text-base tabular-nums">{dec(bd.data.total_amount)} kr</span></span>
              </div>
              <div className="rounded-xl border border-[#e2e8f0] overflow-auto max-h-[60vh]">
                <table className="w-full text-sm min-w-[620px]">
                  <thead className="sticky top-0 bg-[#ffffff]">
                    <tr className="text-left text-slate-500 border-b border-[#e2e8f0]">
                      <th className="px-3 py-2.5 font-medium">Vare</th>
                      <th className="px-3 py-2.5 font-medium">Butik</th>
                      <th className="px-3 py-2.5 font-medium text-right">Antal</th>
                      <th className="px-3 py-2.5 font-medium text-right">Kostpris</th>
                      <th className="px-3 py-2.5 font-medium text-right">Afregning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bd.data.products.map((p) => (
                      <tr key={`${p.store}:${p.productno}`} className="border-b border-[#e2e8f0] last:border-0" data-testid={`breakdown-row-${p.productno}`}>
                        <td className="px-3 py-2 text-slate-700">
                          {p.title}
                          {p.fallback && <span className="ml-1.5 text-[10px] text-amber-600" title="Kostpris fra POS (varen findes ikke i lager-snapshot)">≈</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-500">{p.store_label}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{num(p.qty)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{dec(p.costprice)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-600 font-medium">{dkk(p.amount)}</td>
                      </tr>
                    ))}
                    {bd.data.products.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">Ingen salg i denne uge.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-500">≈ = kostpris hentet fra POS-linjen (varen findes ikke i det aktuelle lager-snapshot). Kostpris vises ekskl. moms; afregning er inkl. moms (×1,25).</p>
              {admin && (
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#e2e8f0]">
                  <p className="text-[11px] text-slate-500">Passer tallet ikke med Customers First? Genopbyg ugens salg rent fra C1st (fjerner evt. akkumulerede dubletter).</p>
                  <Button onClick={() => rebuildWeek(bd.week)} disabled={rebuilding}
                    className="shrink-0 rounded-lg bg-[#ffffff] border border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] hover:text-slate-900 gap-2" data-testid="dangoods-rebuild-week-btn">
                    <RefreshCw className={`w-4 h-4 ${rebuilding ? "animate-spin" : ""}`} /> Genopbyg uge {bd.week?.week_no}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
